import { DateTime } from "luxon";
import { CalendarIcon, ClockIcon, EarthIcon, VideoIcon } from "lucide-react";

import RyanAvatar from "../../images/ryan-avatar.png";

import { Modal } from "../ui/Modal";
import { Input } from "../ui/Input";

import { useMeetingDetailsModal } from "../../hooks/useMeetingDetailsModal";
import { useMeetingDetails } from "../../hooks/useMeetingDetails";
import { useCreateMeeting } from "../../hooks/useCreateMeeting";
import { useMeetingSummaryModal } from "../../hooks/useMeetingSummaryModal";
import { useCreateBooking } from "../../hooks/useCreateBooking";

import { cn, formatMeetingTimeRange } from "../../lib/utils";

export const MeetingDetailsModal = () => {
  const {
    time,
    date,
    userName,
    userEmail,
    userTimezone,
    hostTimezone,
    setUserName,
    setUserEmail,
  } = useMeetingDetails();
  const { isOpen: isMDMOpen, onClose: onMDMClose } = useMeetingDetailsModal();
  const { onOpen: onMSMOpen } = useMeetingSummaryModal();
  const { mutate: createMeeting, isPending } = useCreateMeeting();

  const { mutate: createBooking } = useCreateBooking();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!userName.trim() || !userEmail.trim() || !date || !time) return;

    const userTz = (userTimezone as any).value;

    // 1. Create datetime in USER timezone
    const userDateTime = DateTime.fromJSDate(date, {
      zone: userTz,
    }).set({
      hour: Number(time.split(":")[0]),
      minute: Number(time.split(":")[1]),
    });

    // 2. Convert to UTC (for DB)
    const startUTC = userDateTime.toUTC();
    const endUTC = startUTC.plus({ minutes: 30 });

    // 3. Convert to HOST timezone (for Zoom)
    const hostStart = startUTC.setZone(hostTimezone);

    // Create zoom meeting
    createMeeting(
      {
        startTime: hostStart.toISO()!,
        userTimezone: userTz,
        hostTimezone,
        userName,
        userEmail,
      },
      {
        onSuccess: (data) => {
          // Store newly created zoom meeting
          createBooking({
            userName,
            userEmail,

            startTime: startUTC.toISO()!,
            endTime: endUTC.toISO()!,

            userTimezone: userTz,
            hostTimezone,

            meetingId: data.data.id,
            joinUrl: data.data.join_url,
            meetingDate: date,
          });

          onMDMClose();
          onMSMOpen();
        },
      },
    );
  };

  return (
    <Modal
      isOpen={isMDMOpen}
      onClose={onMDMClose}
      className="flex flex-col md:flex-row"
    >
      <div className="w-full md:max-w-96 p-7 space-y-6 border-b md:border-b-0 md:border-r border-gray-800">
        <div className="space-y-2">
          <img src={RyanAvatar} alt="Ryan" className="size-[100px]" />
          <h1 className="text-[28px] font-bold text-white">Ryan Rayner</h1>
        </div>

        <div className="space-y-3">
          <div className="flex items-center gap-x-2">
            <ClockIcon className="text-white shrink-0" />
            <p className="font-bold text-gray-500">30 min</p>
          </div>

          <div className="flex items-center gap-x-2">
            <VideoIcon className="text-white shrink-0" />
            <p className="font-bold text-gray-500">
              Web conferencing details provided upon confirmation.
            </p>
          </div>

          <div className="flex items-center gap-x-2">
            <CalendarIcon className="text-white shrink-0" />
            <p className="font-bold text-gray-500">
              {formatMeetingTimeRange(time)},{" "}
              {date &&
                date.toLocaleDateString("en-US", {
                  weekday: "long",
                  month: "long",
                  day: "numeric",
                  year: "numeric",
                })}
            </p>
          </div>

          <div className="flex items-center gap-x-2">
            <EarthIcon className="text-white shrink-0" />
            <p className="font-bold text-gray-500">
              {(userTimezone as any).value}
            </p>
          </div>
        </div>
      </div>

      <div className="w-full md:max-w-[420px] p-7 space-y-6">
        <h2 className="text-white font-bold">Enter details</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="Name"
            type="text"
            name="name"
            required
            value={userName}
            onChange={(e) => setUserName(e.target.value)}
          />

          <Input
            label="Email"
            type="email"
            name="email"
            required
            value={userEmail}
            onChange={(e) => setUserEmail(e.target.value)}
          />

          <p className="text-sm text-white">
            By proceeding, you confirm that you have read and agree to{" "}
            <span className="font-bold text-[#01C2F3] underline underline-offset-2">
              Calendly's Terms of Use
            </span>{" "}
            and{" "}
            <span className="font-bold text-[#01C2F3] underline underline-offset-2">
              Privacy Notice.
            </span>
          </p>

          <button
            type="submit"
            disabled={isPending}
            className={cn("glow-button", isPending && "opacity-50")}
          >
            Book a Meeting
          </button>
        </form>
      </div>
    </Modal>
  );
};
